import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col } from 'react-bootstrap';
import { fetchActivitiesQuotesRequest } from '../../store/actions/dataActions';
import { FiFileText, FiRefreshCw, FiEye } from 'react-icons/fi';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminActivities.css';

const EMPTY_ACTIVITIES_QUOTES_FILTERS = {
    reservation_number: '',
};

// Helper function to get nested values
const getDeep = (obj, path, defaultValue = null) => {
    const keys = Array.isArray(path) ? path : path.split('.');
    let result = obj;
    for (const key of keys) {
        if (result == null || typeof result !== 'object') return defaultValue;
        result = result[key];
    }
    return result !== undefined && result !== null ? result : defaultValue;
};

const AdminActivitiesQuotes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { activitiesQuotes } = useSelector((state) => state.data);
    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('activities-quotes', EMPTY_ACTIVITIES_QUOTES_FILTERS);

    // Charger les quotes avec pagination et filtres
    const loadQuotes = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                params[key] = activeFilters[key].trim();
            }
        });

        dispatch(fetchActivitiesQuotesRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        loadQuotes(currentPage);
    }, [currentPage, loadQuotes]);

    const handleRefresh = () => {
        loadQuotes(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadQuotes(1, { ...filters });
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_ACTIVITIES_QUOTES_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadQuotes(1, emptyFilters);
        }, 100);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    // Extraire les données et métadonnées de la réponse API
    // La structure est: { data: [...], meta: {...} }
    const quotesData = activitiesQuotes?.data?.data || [];
    const quotesArray = Array.isArray(quotesData) ? quotesData : [];
    const meta = activitiesQuotes?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + quotesArray.length, totalItems);

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const range = [];
        const maxPages = 5;
        let start = Math.max(1, currentPage - Math.floor(maxPages / 2));
        let end = Math.min(totalPages, start + maxPages - 1);

        if (end - start < maxPages - 1) {
            start = Math.max(1, end - maxPages + 1);
        }

        for (let i = start; i <= end; i++) {
            range.push(i);
        }
        return range;
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderValue = (value, fallback = 'N/A') => {
        if (value === null || value === undefined || value === '') {
            return fallback;
        }
        return value;
    };

    return (
        <div className="dashboard-page admin-activities-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiFileText className="me-2" />
                                Gestion Activities Quotes
                            </h2>
                            <p className="text-muted mb-0">Gérez toutes les quotes d'activités</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" onClick={handleRefresh} disabled={activitiesQuotes.loading}>
                                <FiRefreshCw className={activitiesQuotes.loading ? 'spinning' : ''} /> Actualiser
                            </Button>
                        </div>
                    </div>

                    {activitiesQuotes.error && (
                        <Alert variant="danger" className="mt-3">
                            {activitiesQuotes.error}
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: RES-2024-001"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span>
                                Résultats : {indexOfFirstItem + 1} - {indexOfLastItem} sur {totalItems}
                            </span>
                            {activitiesQuotes.loading && <Spinner animation="border" size="sm" />}
                        </Card.Header>
                        <div className="table-responsive table-scroll">
                            <Table striped hover className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Code Quote</th>
                                        <th>Activité</th>
                                        <th>Partenaire</th>
                                        <th>Période</th>
                                        <th>Prix activité</th>
                                        <th>Prix assurance (HT)</th>
                                        <th>Prix TTC</th>
                                        <th>TSCA</th>
                                        <th>Catégorie</th>
                                        <th className="text-end col-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activitiesQuotes.loading ? (
                                        <tr>
                                            <td colSpan={10} className="text-center py-4">
                                                <Spinner animation="border" role="status" />
                                            </td>
                                        </tr>
                                    ) : quotesArray.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="text-center py-4">
                                                Aucune quote trouvée.
                                            </td>
                                        </tr>
                                    ) : (
                                        quotesArray.map((quote, index) => {
                                            const quoteId = quote.quote_id || quote.id || `quote-${index}`;
                                            const activityInfo = quote.activity_info || {};
                                            const partnerInfo = quote.partner_info || {};
                                            const referenceInfo = quote.reference_info || {};
                                            
                                            return (
                                                <tr key={quoteId}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        <div className="fw-semibold">{renderValue(quote.quote_code)}</div>
                                                        <div className="text-muted small">{quoteId.substring(0, 8)}...</div>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{renderValue(activityInfo.name || referenceInfo.reference_name)}</div>
                                                        <div className="text-muted small">{renderValue(activityInfo.activity_code)}</div>
                                                        {activityInfo.reservation_number && (
                                                            <div className="text-muted small">Réservation: {activityInfo.reservation_number}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{renderValue(partnerInfo.company_name || `${partnerInfo.partner_first_name || ''} ${partnerInfo.partner_last_name || ''}`.trim())}</div>
                                                        <div className="text-muted small">{renderValue(partnerInfo.code)}</div>
                                                    </td>
                                                    <td>
                                                        <div>{renderValue(activityInfo.activity_start_date)}</div>
                                                        <div className="text-muted small">
                                                            {activityInfo.activity_start_time && activityInfo.activity_end_time 
                                                                ? `${activityInfo.activity_start_time.substring(0, 5)} - ${activityInfo.activity_end_time.substring(0, 5)}`
                                                                : renderValue(activityInfo.duration)}
                                                        </div>
                                                        {activityInfo.activity_start_date !== activityInfo.activity_end_date && (
                                                            <div className="text-muted small">Jusqu'au {renderValue(activityInfo.activity_end_date)}</div>
                                                        )}
                                                    </td>
                                                    <td>{renderValue(activityInfo.activity_price)}</td>
                                                    <td>{renderValue(quote.insurance_price_excl_tsca)}</td>
                                                    <td>
                                                        <div className="fw-semibold">{renderValue(quote.price_incl_tsca)} EUR</div>
                                                    </td>
                                                    <td>
                                                        <div>{renderValue(quote.tsca)}%</div>
                                                        <div className="text-muted small">{renderValue(quote.tsca_amount)} EUR</div>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-info">
                                                            {renderValue(activityInfo.category || referenceInfo.reference_category)}
                                                        </span>
                                                    </td>
                                                    <td className="text-end col-actions">
                                                        <Button
                                                            variant="outline-info"
                                                            size="sm"
                                                            onClick={() => navigate(`/activity/quotes/${quoteId}`)}
                                                            title="Détails"
                                                            aria-label="Détails"
                                                        >
                                                            <FiEye />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                            Total : {totalItems} quote(s)
                        </div>
                        <Pagination>
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                            {getPaginationRange().map((page) => (
                                <Pagination.Item
                                    key={page}
                                    active={page === currentPage}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivitiesQuotes;
